import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../../../components/DataGrid/DataGrid';
import { tableReservationService } from '../../../../services/restaurantTableService';
import { CirclePlus, RefreshCw, SquarePen, Trash2, BookOpen, CalendarClock, CheckCircle2 } from 'lucide-react';
import '../RestaurantPage.css';

const STATUS_BADGE: Record<string, string> = {
  Pending:   'ims-badge-pending',
  Confirmed: 'ims-badge-confirmed',
  Seated:    'ims-badge-approved',
  Completed: 'ims-badge-completed',
  Cancelled: 'ims-badge-closed',
};

const STATUSES = ['Pending', 'Confirmed', 'Seated', 'Completed', 'Cancelled'];

const ReservationListPage: React.FC = () => {
  const navigate  = useNavigate();
  const { t }     = useTranslation();
  const [items,   setItems]   = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [dateFilter,   setDateFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const unwrap = (res: any): any[] => {
    const d = res?.data?.data;
    return Array.isArray(d) ? d : (d?.data ?? []);
  };

  const loadItems = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params: any = {};
      if (dateFilter)   params.date   = dateFilter;
      if (statusFilter) params.status = statusFilter;
      setItems(unwrap(await tableReservationService.getAll(params)));
    } catch { setError(t('reservation.failedToLoad', 'Failed to load reservations.')); }
    finally { setLoading(false); }
  }, [t, dateFilter, statusFilter]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const params: any = {};
      if (dateFilter)   params.date   = dateFilter;
      if (statusFilter) params.status = statusFilter;
      setItems(unwrap(await tableReservationService.getAll(params)));
    } catch { setError(t('reservation.failedToLoad', 'Failed to load reservations.')); }
    finally { setRefreshing(false); }
  };

  const handleDelete = async (row: any) => {
    if (!window.confirm(t('reservation.deleteConfirm', 'Cancel this reservation?'))) return;
    try {
      await tableReservationService.delete(row.id);
      setItems(prev => prev.filter(i => i.id !== row.id));
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? t('common.error'));
    }
  };

  const columns: GridColumn[] = [
    { key: 'tableNumber', label: t('table.tableNumber'), sortable: true, width: '100px',
      render: (v) => <strong>{v ?? '—'}</strong> },
    { key: 'tableName',   label: t('table.tableName'),   sortable: true, width: '140px' },
    { key: 'customerName', label: t('reservation.customerName'), sortable: true, width: '160px' },
    { key: 'customerPhone', label: t('reservation.customerPhone'), width: '120px',
      render: (v) => <span>{v ?? '—'}</span> },
    { key: 'partySize',    label: t('reservation.partySize'), sortable: true, width: '90px' },
    { key: 'reservationDate', label: t('reservation.date'), sortable: true, width: '120px' },
    { key: 'reservationTime', label: t('reservation.time'), width: '80px' },
    { key: 'durationMinutes', label: t('reservation.duration', 'Duration'), width: '90px',
      render: (v) => <span>{v} min</span> },
    { key: 'status', label: t('common.status'), width: '110px',
      render: (v) => <span className={`ims-badge ${STATUS_BADGE[v] ?? 'ims-badge-draft'}`}>{t(`reservation.statuses.${v}`, v)}</span> },
  ];

  const actions: GridAction[] = [
    { icon: <SquarePen size={16} />, onClick: (r) => navigate(`/restaurant/reservations/${r.id}/edit`),
      className: 'grid-action-btn-edit', title: t('common.edit'),
      hidden: (r) => r.status === 'Cancelled' || r.status === 'Completed' },
    { icon: <Trash2 size={16} />, onClick: handleDelete,
      className: 'grid-action-btn-delete', title: t('reservation.cancel', 'Cancel'),
      hidden: (r) => r.status === 'Cancelled' || r.status === 'Seated' || r.status === 'Completed' },
  ];

  const todayStr = new Date().toISOString().slice(0, 10);
  const todayCount    = items.filter(i => i.reservationDate === todayStr).length;
  const confirmedCount = items.filter(i => i.status === 'Confirmed').length;

  return (
    <Layout>
      <div className="ims-page rest-page">
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">{t('reservation.title')}</h1>
            <p className="ims-list-subtitle">{t('reservation.subtitle', 'Manage table reservations')}</p>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw size={15} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
            <button className="btn-new" onClick={() => navigate('/restaurant/reservations/create')}>
              <CirclePlus size={16} /> {t('reservation.newReservation')}
            </button>
          </div>
        </div>

        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon rest-icon-orange"><BookOpen size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{items.length}</span>
              <span className="ims-stat-label">{t('reservation.total', 'Total')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon rest-icon-green"><CalendarClock size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{todayCount}</span>
              <span className="ims-stat-label">{t('reservation.today', "Today's")}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon" style={{ background: 'var(--ims-blue-bg)', color: 'var(--ims-blue-text)' }}><CheckCircle2 size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{confirmedCount}</span>
              <span className="ims-stat-label">{t('reservation.confirmed', 'Confirmed')}</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="ims-card" style={{ marginBottom: 12 }}>
          <div className="ims-card-body" style={{ padding: '0.75rem 1.25rem', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, color: 'var(--ims-text-sub)' }}>{t('reservation.filterDate', 'Date')}</label>
              <input type="date" className="ims-form-control" value={dateFilter}
                onChange={e => setDateFilter(e.target.value)} style={{ width: 180 }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, color: 'var(--ims-text-sub)' }}>{t('common.status')}</label>
              <select className="ims-form-control" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: 160 }}>
                <option value="">{t('common.all', 'All')}</option>
                {STATUSES.map(s => <option key={s} value={s}>{t(`reservation.statuses.${s}`, s)}</option>)}
              </select>
            </div>
            {(dateFilter || statusFilter) && (
              <button className="ims-btn ims-btn-secondary" style={{ alignSelf: 'flex-end' }}
                onClick={() => { setDateFilter(''); setStatusFilter(''); }}>
                {t('common.clearFilters', 'Clear')}
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="ims-alert ims-alert-error">
            {error}
            <button className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="ims-grid-wrapper">
          <DataGrid data={items} columns={columns} actions={actions} loading={loading}
            pageSize={10} searchPlaceholder={t('common.search')}
            emptyMessage={t('reservation.noReservations', 'No reservations found.')}
            rowsPerPageOptions={[5, 10, 25, 50]} />
        </div>
      </div>
    </Layout>
  );
};

export default ReservationListPage;
